import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, Users, Briefcase, MessageSquare, LogOut, 
  Brain, Settings, Bell, Search, Calendar, Clock,
  CheckCircle, FileText, Mail, Phone, Plus, ChevronRight,
  Inbox, Send, Archive, Trash, Paperclip, Image
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';

const translations = {
  es: {
    title: 'Espacio de Trabajo Virtual',
    subtitle: 'Tu entorno laboral digital',
    sections: {
      email: 'Correo',
      calendar: 'Calendario',
      tasks: 'Tareas',
      chat: 'Chat',
      files: 'Archivos'
    },
    email: {
      compose: 'Redactar',
      inbox: 'Bandeja de entrada',
      sent: 'Enviados',
      drafts: 'Borradores',
      archive: 'Archivo',
      search: 'Buscar correos...'
    },
    calendar: {
      today: 'Hoy',
      upcoming: 'Próximos eventos',
      schedule: 'Agendar'
    },
    tasks: {
      new: 'Nueva tarea',
      due: 'Vencimiento',
      priority: 'Prioridad'
    }
  },
  en: {
    title: 'Virtual Workspace',
    subtitle: 'Your digital work environment',
    sections: {
      email: 'Email',
      calendar: 'Calendar',
      tasks: 'Tasks',
      chat: 'Chat',
      files: 'Files'
    },
    email: {
      compose: 'Compose',
      inbox: 'Inbox',
      sent: 'Sent',
      drafts: 'Drafts',
      archive: 'Archive',
      search: 'Search emails...'
    },
    calendar: {
      today: 'Today',
      upcoming: 'Upcoming events',
      schedule: 'Schedule'
    },
    tasks: {
      new: 'New task',
      due: 'Due date',
      priority: 'Priority'
    }
  }
};

// Mock data
const mockEmails = [
  {
    id: 1,
    from: 'Sarah Chen',
    subject: 'Project Update: Q1 Goals',
    preview: 'Here are the updated project milestones for Q1...',
    date: '2025-03-14T10:30:00',
    unread: true,
    important: true
  },
  {
    id: 2,
    from: 'Alex Kim',
    subject: 'Team Meeting Notes',
    preview: 'Summary of our discussion regarding the new feature...',
    date: '2025-03-14T09:15:00',
    unread: false,
    important: false
  }
];

const mockEvents = [
  {
    id: 1,
    title: 'Team Standup',
    start: '2025-03-14T09:00:00',
    duration: '15m',
    attendees: 8,
    type: 'recurring'
  },
  {
    id: 2,
    title: 'Project Review',
    start: '2025-03-14T14:00:00',
    duration: '1h',
    attendees: 5,
    type: 'single'
  }
];

const mockTasks = [
  {
    id: 1,
    title: 'Review Q1 Reports',
    dueDate: '2025-03-15',
    priority: 'high',
    status: 'pending'
  },
  {
    id: 2,
    title: 'Update Documentation',
    dueDate: '2025-03-16',
    priority: 'medium',
    status: 'in-progress'
  }
];

const VirtualWorkspacePage = () => {
  const [activeSection, setActiveSection] = useState('email');
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language];
  const dateLocale = language === 'es' ? es : enUS;

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-background border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <Building2 className="h-8 w-8 text-accent" />
                <span className="ml-2 text-xl font-bold text-foreground">
                  Virtual Office
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search..."
                  className="pl-10 pr-4 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                />
                <Search className="absolute left-3 top-2.5 h-5 w-5 text-foreground/50" />
              </div>
              <button className="text-foreground/70 hover:text-foreground relative">
                <Bell className="h-5 w-5" />
                <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-500"></span>
              </button>
              <button className="text-foreground/70 hover:text-foreground">
                <Settings className="h-5 w-5" />
              </button>
              <div className="border-l border-border h-6"></div>
              <div className="flex items-center">
                <div className="h-8 w-8 rounded-full bg-accent/10 flex items-center justify-center text-accent font-medium">
                  {user?.user_metadata?.name?.[0] || 'U'}
                </div>
                <button 
                  onClick={handleSignOut}
                  className="ml-4 text-foreground/70 hover:text-foreground"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-12 gap-8">
          {/* Sidebar */}
          <div className="col-span-3 space-y-8">
            {/* Quick Actions */}
            <div className="dark-card p-4">
              <button className="w-full mb-4 inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent/90">
                <Plus className="h-4 w-4 mr-2" />
                {t.email.compose}
              </button>

              <nav className="space-y-1">
                <button className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg bg-accent/10 text-accent">
                  <Inbox className="h-5 w-5 mr-3" />
                  {t.email.inbox}
                  <span className="ml-auto bg-accent text-white px-2 py-0.5 rounded-full text-xs">
                    3
                  </span>
                </button>
                <button className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg text-foreground hover:bg-secondary/30">
                  <Send className="h-5 w-5 mr-3" />
                  {t.email.sent}
                </button>
                <button className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg text-foreground hover:bg-secondary/30">
                  <Archive className="h-5 w-5 mr-3" />
                  {t.email.archive}
                </button>
                <button className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg text-foreground hover:bg-secondary/30">
                  <Trash className="h-5 w-5 mr-3" />
                  Trash
                </button>
              </nav>
            </div>

            {/* Calendar */}
            <div className="dark-card p-4">
              <h3 className="text-sm font-medium text-foreground/70 uppercase tracking-wider mb-4">
                {t.calendar.upcoming}
              </h3>
              <div className="space-y-4">
                {mockEvents.map((event) => (
                  <div key={event.id} className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-foreground">{event.title}</p>
                      <div className="flex items-center text-xs text-foreground/70">
                        <Clock className="h-3 w-3 mr-1" />
                        {format(new Date(event.start), 'p', { locale: dateLocale })} · {event.duration}
                      </div>
                    </div>
                    <div className="flex items-center">
                      <Users className="h-4 w-4 text-foreground/50 mr-1" />
                      <span className="text-xs text-foreground/70">{event.attendees}</span>
                    </div>
                  </div>
                ))}
                <button className="w-full text-center text-sm text-accent hover:text-accent/90">
                  {t.calendar.schedule}
                </button>
              </div>
            </div>

            {/* Tasks */}
            <div className="dark-card p-4">
              <h3 className="text-sm font-medium text-foreground/70 uppercase tracking-wider mb-4">
                {t.sections.tasks}
              </h3>
              <div className="space-y-4">
                {mockTasks.map((task) => (
                  <div key={task.id} className="flex items-start">
                    <div className={`h-5 w-5 rounded-full border flex items-center justify-center ${
                      task.status === 'completed'
                        ? 'bg-green-500 border-green-500 text-white'
                        : 'border-border'
                    }`}>
                      {task.status === 'completed' && <CheckCircle className="h-3 w-3" />}
                    </div>
                    <div className="ml-3 flex-1">
                      <p className="text-sm font-medium text-foreground">{task.title}</p>
                      <p className="text-xs text-foreground/70">
                        {format(new Date(task.dueDate), 'PP', { locale: dateLocale })}
                      </p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      task.priority === 'high'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {task.priority}
                    </span>
                  </div>
                ))}
                <button className="w-full text-center text-sm text-accent hover:text-accent/90">
                  {t.tasks.new}
                </button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="col-span-9">
            <div className="dark-card">
              {/* Email List */}
              <div className="divide-y divide-border">
                {mockEmails.map((email) => (
                  <div
                    key={email.id}
                    className={`p-4 hover:bg-secondary/30 cursor-pointer ${
                      email.unread ? 'bg-secondary/10' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center min-w-0">
                        <div className="h-8 w-8 rounded-full bg-accent/10 flex items-center justify-center text-accent">
                          {email.from[0]}
                        </div>
                        <div className="ml-3 flex-1 min-w-0">
                          <div className="flex items-center">
                            <p className={`text-sm font-medium ${
                              email.unread ? 'text-foreground' : 'text-foreground/70'
                            } truncate`}>
                              {email.from}
                            </p>
                            <span className="ml-2 flex-shrink-0 text-xs text-foreground/50">
                              {format(new Date(email.date), 'p', { locale: dateLocale })}
                            </span>
                          </div>
                          <p className={`text-sm ${
                            email.unread ? 'text-foreground' : 'text-foreground/70'
                          } truncate`}>
                            {email.subject}
                          </p>
                          <p className="text-sm text-foreground/50 truncate">
                            {email.preview}
                          </p>
                        </div>
                      </div>
                      <div className="ml-4 flex items-center space-x-2">
                        {email.important && (
                          <Star className="h-5 w-5 text-yellow-500" />
                        )}
                        <button className="text-foreground/50 hover:text-foreground">
                          <Archive className="h-5 w-5" />
                        </button>
                        <button className="text-foreground/50 hover:text-foreground">
                          <Trash className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Compose Email */}
              <div className="p-4 border-t border-border">
                <div className="flex items-center space-x-4">
                  <button className="text-foreground/50 hover:text-foreground">
                    <Paperclip className="h-5 w-5" />
                  </button>
                  <button className="text-foreground/50 hover:text-foreground">
                    <Image className="h-5 w-5" />
                  </button>
                  <input
                    type="text"
                    placeholder="Write a message..."
                    className="flex-1 bg-secondary/30 border border-border rounded-lg px-4 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                  <button className="text-accent hover:text-accent/90">
                    <Send className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VirtualWorkspacePage;